import { formatToYYYY_MMM_DD, formatTimeFromISO } from "../../../../helpers/functions";
import { GetDropDownValue } from "../../components/data";
import { followUpTopicOptions, reasonStartOptions } from "../../../patient/clinical-services/service-steps/education/data";
import GoalsSection from "./GoalsSection";

const formatFollowUpLine = (education) => {
  if (!education?.followUpDate && !education?.followUp) {
    return { date: "No follow up appointment", time: "—" };
  }

  if (education?.followUpDate) {
    return {
      date: formatToYYYY_MMM_DD(education.followUpDate),
      time: formatTimeFromISO(education.followUpDate) || "—",
    };
  }

  return {
    date: education?.followUp ? education.followUp : "No follow up appointment",
    time: "—",
  };
};

const formatVaccineRow = (vaccine) => {
  if (!vaccine?.bookingDate) {
    return { date: "Not scheduled", time: "—" };
  }

  return {
    date: formatToYYYY_MMM_DD(vaccine.bookingDate),
    time: formatTimeFromISO(vaccine.bookingDate) || "—",
  };
};

const formatFollowUpTopics = (vals = []) =>
  (Array.isArray(vals) ? vals.filter(Boolean).filter((v) => String(v).toLowerCase() !== "nofollowup").map((v) => GetDropDownValue(followUpTopicOptions, v) ?? v) : []);

const getFollowUpDetails = (education) => {
  if (!education) return "—";
  return (
    GetDropDownValue(reasonStartOptions, education.followUp) ||
    formatFollowUpTopics(education.followUpType ?? []).join(", ") ||
    "—"
  );
};

export const AppointmentInfo = ({ documentation, education, minorAilmentName, goals, treatment }) => {
  const followUpRow = formatFollowUpLine(education);
  const vaccineAppointments = education?.vaccineSchedule ?? [];

  return (
    <>
      {/* Horizontal Minor Ailment cards are rendered elsewhere; do not render here */}
      <div className="mb-10">
        <GoalsSection
          personalGoals={
            goals?.personalGoals ?? treatment?.personalGoals ?? documentation?.treatment?.personalGoals ?? ""
          }
          howToReachGoals={
            goals?.howToAchieveGoals ?? treatment?.howToAchieveGoals ?? documentation?.treatment?.howToAchieveGoals ?? ""
          }
        />
      </div>
      <div className="flex flex-col mb-4 pl-2">
        <p className="text-teal-300 font-bold text-[0.25714286in]">
          Appointments for Follow Up
        </p>
        <div className="text-lg text-teal-300">
          <span className="font-bold">Referral Type:</span>{" "}
          {documentation?.referralType?.join(", ")}
        </div>
      </div>
      {/* <div className="bg-gray-20 px-[0.22857143in] text-teal-300 py-[0.11428571in] rounded-[0.14285714in] w-full flex flex-row gap-[0.17142857in] text-[0.14285714in] font-medium">
        
      </div> */}
      <FollowUpTableBody
        followUpRow={followUpRow}
        vaccineAppointments={vaccineAppointments}
        minorAilmentName={minorAilmentName}
        education={education}
      />
    </>
  );
};

export const HeaderInfo = ({ minorAilmentName }) => {
  return (
    <>
      <img 
        // src={logoHealthCompass} 
        src="/icons/logos/health-compass.svg"
        className="w-[158px] h-auto" 
      />
      <div className="flex flex-row justify-between items-center mb-[36px]">
        <p className="text-teal-300 font-bold text-[20px]">
          Minor Ailment: {minorAilmentName}
        </p>
        <div className="bg-gray-10 font-bold border-gray-600 border-[0.014285714in] rounded-[0.057142857in] px-[0.17142857in] py-[0.085714286in] text-[0.11428571in] text-gray-600">
          Pharmacy Name/Logo
        </div>
      </div>
    </>
  );
};

export const FollowUpTable = ({ documentation, education, minorAilmentName }) => {
  const followUpRow = formatFollowUpLine(education);
  const vaccineAppointments = education?.vaccineSchedule ?? [];

  return (
    <>
      <div className="flex flex-col mb-4 pl-2">
        <p className="text-teal-300 font-bold text-[0.25714286in]">Appointments for Follow Up</p>
        <div className="text-lg text-teal-300">
          <span className="font-bold">Referral Type:</span>{" "}
          {documentation?.referralType?.join(", ")}
        </div>
      </div>
      <FollowUpTableBody
        followUpRow={followUpRow}
        vaccineAppointments={vaccineAppointments}
        minorAilmentName={minorAilmentName}
        education={education}
      />
    </>
  );
};

const FollowUpTableBody = ({ followUpRow, vaccineAppointments = [], minorAilmentName, education }) => (
  <div className="border border-gray-100 rounded-xl bg-white">
    <table className="text-base w-full border-0 table-fixed">
      <thead className="bg-gray-20">
        <tr className="text-left text-teal-300">
          <th className="font-bold p-3 w-[28%] whitespace-nowrap">Date</th>
          <th className="font-bold p-3 w-[14%] whitespace-nowrap">Time</th>
          <th className="font-bold p-3 w-[30%] pl-5">Appointment Type</th>
          <th className="font-bold p-3 w-[28%] pl-5">Details</th>
        </tr>
      </thead>
      <tbody>
        <tr className="align-top">
          <td className="p-4 w-[28%] whitespace-nowrap align-top">{followUpRow.date}</td>
          <td className="p-4 w-[14%] text-left whitespace-nowrap align-top">{followUpRow.time}</td>
          <td className="p-4 w-[30%] pl-5 align-top">
            <div className="flex flex-col gap-0.5 whitespace-normal">
              <div>{(minorAilmentName || "Allergic Rhinitis").trim()}</div>
              <div>Follow Up</div>
            </div>
          </td>
          <td className="p-4 w-[28%] pl-5 align-top whitespace-normal">{getFollowUpDetails(education)}</td>
        </tr>
        {vaccineAppointments.length > 0 &&
          vaccineAppointments.map((vaccine) => {
            const row = formatVaccineRow(vaccine);
            return (
              <tr className="" key={`${vaccine.name}-${vaccine.bookingDate}`}>
                <td className="p-1">{row.date}</td>
                <td className="p-1">{row.time}</td>
                <td className="p-1">Vaccine Health</td>
                <td className="p-1">{vaccine?.name ? `${vaccine.name} Vaccine` : "—"}</td>
              </tr>
            );
          })}
      </tbody>
    </table>
  </div>
);
